import { useParams, useSearchParams } from "react-router-dom";

import { FLASHPAY_ROLES, RequireRole, useRealmRoles } from "@/core/auth";
import { LoadScreen, PageBackButton } from "@/core/components/shared";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/core/components/ui/tabs";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { useMerchant } from "@/features/merchant/api/use-merchant";
import {
  FeeOverridesSection,
  MerchantAdminActions,
  MerchantSalesSection,
  MerchantStoresSection,
  QrCodesSection,
  SalesAgentsSection,
  SettlementConfigSection,
  SubMerchantsSection,
} from "@/features/merchant/components";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";
import type { MerchantStatus } from "@/features/shared/api/enums";

const STATUS_VARIANTS: Record<
  MerchantStatus,
  "warning" | "success" | "destructive" | "secondary"
> = {
  PENDING: "warning",
  ACTIVE: "success",
  SUSPENDED: "destructive",
  CLOSED: "secondary",
};

/** Actions de l'onglet Informations : KYC, reversement, permissions. */
const MERCHANT_INFO_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

/** Liste `/app/merchants` : le marchand titulaire n'y a pas accès. */
const MERCHANT_LIST_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
] as const;

/** Onglets self-service du détail (valeurs `Tabs`, hors Informations). */
const SELF_SERVICE_TABS = [
  "stores",
  "qr-codes",
  "sales-agents",
  "sales",
  "settlement",
  "fee-overrides",
  "sub-merchants",
] as const;

type SelfServiceTab = (typeof SELF_SERVICE_TABS)[number];

const isSelfServiceTab = (value: string): value is SelfServiceTab =>
  (SELF_SERVICE_TABS as readonly string[]).includes(value);

/**
 * Onglet affiché : `?tab=` s'il correspond à un onglet visible, sinon
 * Informations (back-office) ou Points de vente (marchand). Une valeur
 * inconnue, y compris `info` pour un marchand, est ignorée.
 */
const resolveMerchantTab = (
  requested: string | null,
  canAdminMerchant: boolean
): "info" | SelfServiceTab => {
  if (requested === "info" && canAdminMerchant) return "info";
  if (requested && isSelfServiceTab(requested)) return requested;
  return canAdminMerchant ? "info" : "stores";
};

/**
 * Détail d'un marchand (`GET /Merchants/{id}`) — onglets : Informations,
 * Stores, QR codes, Sales agents, Ventes, Reversement, Fee overrides,
 * Sous-marchands. Toutes les données financières / identifiants en
 * `NoTranslate`.
 */
export function MerchantDetailPage({
  merchantId: merchantIdProp,
}: {
  /** Identifiant déjà résolu (page `/app/merchants/me`). Sinon, paramètre de route. */
  merchantId?: string;
} = {}) {
  const { t } = useTypedTranslation();
  const { t: tm } = useMerchantTranslation();
  const { merchantId: merchantIdParam = "" } = useParams();
  const merchantId = merchantIdProp || merchantIdParam;
  const [searchParams, setSearchParams] = useSearchParams();
  const realmRoles = useRealmRoles();
  const canOpenMerchantList = MERCHANT_LIST_ROLES.some((role) =>
    realmRoles.includes(role)
  );
  const canAdminMerchant = MERCHANT_INFO_ROLES.some((role) =>
    realmRoles.includes(role)
  );
  const { data: merchant, isLoading, error, refetch } = useMerchant(merchantId);
  const tab = resolveMerchantTab(searchParams.get("tab"), canAdminMerchant);
  const backButton = canOpenMerchantList ? (
    <PageBackButton to="/app/merchants" label={t("common.back")} />
  ) : null;

  if (isLoading) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex h-64 items-center justify-center">
          <LoadScreen />
        </div>
      </section>
    );
  }

  if (error || !merchant) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex flex-col items-center gap-4 py-24">
          <p className="font-semibold text-destructive">
            {error?.message ?? t("error")}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t("reload")}
          </Button>
        </div>
      </section>
    );
  }

  const fields: { label: string; value?: string }[] = [
    { label: tm("merchant.merchants.detail.id"), value: merchant.id },
    {
      label: tm("merchant.merchants.columns.ownerPartyId"),
      value: merchant.ownerPartyId,
    },
    {
      label: tm("merchant.merchants.detail.parentMerchantId"),
      value: merchant.parentMerchantId ?? undefined,
    },
    {
      label: tm("merchant.merchants.detail.settlementWalletId"),
      value: merchant.settlementWalletId ?? undefined,
    },
    {
      label: tm("merchant.merchants.detail.createdAt"),
      value: merchant.createdAt
        ? formatDateWithFallback(merchant.createdAt, "—")
        : undefined,
    },
    {
      label: tm("merchant.merchants.detail.updatedAt"),
      value: merchant.updatedAt
        ? formatDateWithFallback(merchant.updatedAt, "—")
        : undefined,
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {backButton}
          <h1 className="text-2xl font-semibold text-foreground">
            <NoTranslate as="span">
              {merchant.tradeName || merchant.legalName || merchant.id}
            </NoTranslate>
          </h1>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <NoTranslate as="span">
              <Badge variant="secondary">
                {merchant.merchantType ?? "—"}
              </Badge>
            </NoTranslate>
            {merchant.status ? (
              <NoTranslate as="span">
                <Badge
                  variant={STATUS_VARIANTS[merchant.status] ?? "outline"}
                >
                  {merchant.status}
                </Badge>
              </NoTranslate>
            ) : null}
            {merchant.kycStatus ? (
              <NoTranslate as="span">
                <Badge variant="outline">{merchant.kycStatus}</Badge>
              </NoTranslate>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map(({ label, value }) => (
              <div key={label}>
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd className="font-medium">
                  <NoTranslate as="span" className="break-all">
                    {value || "—"}
                  </NoTranslate>
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <Tabs
        value={tab}
        onValueChange={(value) => {
          const next = new URLSearchParams(searchParams);
          next.set("tab", value);
          setSearchParams(next, { replace: true });
        }}
        className="w-full"
      >
        <TabsList className="flex h-auto w-full flex-wrap">
          {canAdminMerchant ? (
            <TabsTrigger value="info">
              {tm("merchant.merchants.tabs.info")}
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="stores">
            {tm("merchant.merchants.tabs.stores")}
          </TabsTrigger>
          <TabsTrigger value="qr-codes">
            {tm("merchant.merchants.tabs.qrCodes")}
          </TabsTrigger>
          <TabsTrigger value="sales-agents">
            {tm("merchant.merchants.tabs.salesAgents")}
          </TabsTrigger>
          <TabsTrigger value="sales">
            {tm("merchant.merchants.tabs.sales")}
          </TabsTrigger>
          <TabsTrigger value="settlement">
            {tm("merchant.merchants.tabs.settlement")}
          </TabsTrigger>
          <TabsTrigger value="fee-overrides">
            {tm("merchant.merchants.tabs.feeOverrides")}
          </TabsTrigger>
          <TabsTrigger value="sub-merchants">
            {tm("merchant.merchants.tabs.subMerchants")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-4">
          <RequireRole roles={MERCHANT_INFO_ROLES}>
            <MerchantAdminActions merchant={merchant} />
          </RequireRole>
        </TabsContent>
        <TabsContent value="stores" className="mt-4">
          <MerchantStoresSection merchantId={merchantId} />
        </TabsContent>
        <TabsContent value="qr-codes" className="mt-4">
          <QrCodesSection key={merchantId} merchantId={merchantId} />
        </TabsContent>
        <TabsContent value="sales-agents" className="mt-4">
          <SalesAgentsSection merchantId={merchantId} />
        </TabsContent>
        <TabsContent value="sales" className="mt-4">
          <MerchantSalesSection merchantId={merchantId} />
        </TabsContent>
        <TabsContent value="settlement" className="mt-4">
          <SettlementConfigSection merchantId={merchantId} />
        </TabsContent>
        <TabsContent value="fee-overrides" className="mt-4">
          <FeeOverridesSection merchantId={merchantId} />
        </TabsContent>
        <TabsContent value="sub-merchants" className="mt-4">
          <SubMerchantsSection masterId={merchantId} />
        </TabsContent>
      </Tabs>
    </section>
  );
}
